import { ChangeDetectionStrategy, Component, HostListener, inject, signal } from '@angular/core';
import { CurrentUserService } from '@/app/core/auth/current-user.service';
import { NavIcons } from '@/app/shared/nav/nav-icons';
import { SafeHtmlPipe } from '@/app/shared/ui/pipes/safe-html.pipe';
import { OrganizationSelectorComponent } from './organization-selector.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [SafeHtmlPipe, OrganizationSelectorComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'block [grid-area:header] min-w-0',
  },
  template: `
    <header
      class="flex h-[var(--header-h)] items-center gap-4 bg-brand-primary px-6 shadow-lg sticky top-0 z-[100]"
    >
      <div class="flex min-w-0 items-center gap-2">
        <span
          class="h-8 w-[38px] shrink-0 bg-no-repeat"
          style="background-image: url('/logos/logo_white.png'); background-size: 178.7px 64.6px; background-position: -7.3px -3.6px;"
          aria-hidden="true"
        ></span>
        <div class="min-w-0 leading-tight">
          <p class="m-0 truncate text-[15px] font-bold text-white">Portal de Prestadores</p>
          <p
            class="m-0 truncate text-[10px] font-semibold uppercase tracking-[0.08em] text-white/60"
          >
            Isapre Colmena
          </p>
        </div>
      </div>

      <div class="flex-1"></div>

      <app-organization-selector />

      <div class="flex items-center gap-2 text-white/70" title="Notificaciones">
        <span class="relative inline-flex h-5 w-5 shrink-0">
          <span class="[&_svg]:h-5 [&_svg]:w-5" [innerHTML]="icons.bell | safeHtml"></span>
          <span
            class="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-success ring-2 ring-brand-secondary"
            aria-hidden="true"
          ></span>
        </span>
        <span class="text-[11px] font-semibold uppercase tracking-[0.06em]">Notificaciones</span>
      </div>

      <div class="relative">
        <button
          type="button"
          class="flex items-center gap-2.5 !rounded-full border border-border/20 py-1 pl-1 pr-2.5 text-left transition-colors duration-150 hover:bg-white/10"
          [attr.aria-expanded]="menuOpen()"
          aria-haspopup="true"
          (click)="toggleMenu()"
        >
          <span
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-primary text-[13px] font-bold text-white"
            >{{ user.initials }}</span
          >
          <span class="hidden min-w-0 leading-tight sm:block">
            <span class="block truncate text-[13px] font-semibold text-white">{{
              user.displayName
            }}</span>
            <span
              class="block truncate text-[10px] font-semibold uppercase tracking-[0.06em] text-white/60"
              >{{ user.roleLabel }}</span
            >
          </span>
          <span
            class="flex shrink-0 text-white/60 transition-transform duration-150 [&_svg]:h-4 [&_svg]:w-4"
            [class.rotate-180]="menuOpen()"
            [innerHTML]="icons.chevronDown | safeHtml"
          ></span>
        </button>

        @if (menuOpen()) {
          <button
            type="button"
            class="fixed inset-0 z-[90] cursor-default border-0 bg-transparent p-0"
            aria-label="Cerrar menú"
            (click)="closeMenu()"
          ></button>
          <div
            class="absolute right-0 top-[calc(100%+0.5rem)] z-[100] w-64 overflow-hidden rounded-sm border border-border bg-surface shadow"
            role="menu"
          >
            <div class="border-b border-border px-4 py-3">
              <p class="m-0 truncate text-sm font-semibold text-text">{{ user.displayName }}</p>
              <p class="m-0 truncate text-xs text-muted">{{ user.email }}</p>
            </div>
            <button
              type="button"
              class="block w-full cursor-pointer border-0 bg-transparent px-4 py-2.5 text-left text-sm text-text hover:bg-surface-2"
              role="menuitem"
              (click)="closeMenu()"
            >
              Mi cuenta
            </button>
            <button
              type="button"
              class="block w-full cursor-pointer border-0 bg-transparent px-4 py-2.5 text-left text-sm text-text hover:bg-surface-2"
              role="menuitem"
              (click)="closeMenu()"
            >
              Cambiar contraseña (Entra ID)
            </button>
            <button
              type="button"
              class="block w-full cursor-pointer border-0 border-t border-border bg-transparent px-4 py-2.5 text-left text-sm text-danger hover:bg-danger-subtle"
              role="menuitem"
              (click)="logout()"
            >
              Cerrar sesión
            </button>
          </div>
        }
      </div>
    </header>
  `,
})
export class HeaderComponent {
  protected readonly icons = NavIcons;
  protected readonly user = inject(CurrentUserService);
  protected readonly menuOpen = signal(false);

  protected toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.closeMenu();
  }

  protected logout(): void {
    this.user.logout();
  }
}
